import { useState } from "react";
import { Plus } from "lucide-react";
import { useToast } from "@/components/v2/Toast";
import { useSeedServices, type SeedTemplateKey } from "@/lib/service-seed";
import { cn } from "@/lib/utils";
import { useRegisterStep } from "./OnboardingShell";
import { TEMPLATES } from "./templates";
import { StepShell, StepBody } from "./_StepShell";
import { StepHero } from "./_StepHero";
import { StepFooter } from "./_StepFooter";

/**
 * Step 4 — Pick a starter pack.
 *
 * The operator picks one industry template (or "Blank" to do their own
 * thing later). On Continue, the chosen template's services are seeded
 * via `POST /api/v1/services/seed` in one transaction.
 */
type Selection = SeedTemplateKey | "blank" | null;

export function ServicesStep() {
  const seed = useSeedServices();
  const toast = useToast();
  const [selected, setSelected] = useState<Selection>(null);

  useRegisterStep({
    onContinue: async () => {
      if (selected === null) return;
      if (selected === "blank") return;
      try {
        const res = await seed.mutateAsync(selected);
        const created =
          res.customFieldsCreated + res.tierItemsCreated + res.addonItemsCreated;
        if (created === 0) {
          toast.success(
            "Already set up",
            `${res.service.name} is ready to go in your workspace.`,
          );
        } else {
          const parts: string[] = [];
          if (res.tierItemsCreated > 0)
            parts.push(`${res.tierItemsCreated} pricing tier${res.tierItemsCreated === 1 ? "" : "s"}`);
          if (res.addonItemsCreated > 0)
            parts.push(`${res.addonItemsCreated} add-on${res.addonItemsCreated === 1 ? "" : "s"}`);
          if (res.customFieldsCreated > 0)
            parts.push(`${res.customFieldsCreated} booking field${res.customFieldsCreated === 1 ? "" : "s"}`);
          toast.success(
            `${res.service.name} is ready`,
            parts.length
              ? `Seeded ${parts.join(", ")}. Edit anything in /services.`
              : "Edit it in /services.",
          );
        }
      } catch (e) {
        toast.error("Couldn't seed services", (e as Error).message);
        throw e;
      }
    },
  });

  return (
    <StepShell variant="wide">
      <StepHero
        eyebrow="Step 4 of 5"
        title="Pick a starter pack"
        subtitle="We'll create one ready-to-go service so you have something live on day one. You can edit, delete, or add more later."
      />

      <StepBody>
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          {TEMPLATES.map((t) => {
            const Icon = t.icon;
            const active = selected === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setSelected(t.key)}
                className={cn(
                  "rounded-xl border bg-card p-4 text-left transition-colors flex flex-col h-full",
                  active
                    ? "border-primary bg-primary-light/40 ring-2 ring-primary/30"
                    : "border-border hover:bg-muted/40",
                )}
              >
                <div className="flex items-center gap-2.5 mb-3">
                  <div
                    className={cn(
                      "h-8 w-8 rounded-md inline-flex items-center justify-center shrink-0",
                      active
                        ? "bg-primary text-primary-foreground"
                        : "bg-primary-light/50 text-primary",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <p className="text-[14px] font-semibold tracking-tight">
                    {t.label}
                  </p>
                </div>
                <p className="text-[12px] text-muted-foreground mb-3 leading-relaxed">
                  {t.description}
                </p>
                <ul className="text-[11px] text-muted-foreground space-y-1 mt-auto">
                  {t.preview.map((line) => (
                    <li key={line} className="truncate">
                      · {line}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => setSelected("blank")}
            className={cn(
              "rounded-xl border-2 border-dashed bg-card p-4 text-left transition-colors flex flex-col h-full",
              selected === "blank"
                ? "border-primary bg-primary-light/40"
                : "border-border hover:bg-muted/40",
            )}
          >
            <div className="flex items-center gap-2.5 mb-3">
              <div className="h-8 w-8 rounded-md bg-muted text-muted-foreground inline-flex items-center justify-center shrink-0">
                <Plus className="h-4 w-4" />
              </div>
              <p className="text-[14px] font-semibold tracking-tight">
                Start blank
              </p>
            </div>
            <p className="text-[12px] text-muted-foreground mt-auto leading-relaxed">
              Set up services your own way later. We won't add anything for you.
            </p>
          </button>
        </div>
      </StepBody>

      <StepFooter />
    </StepShell>
  );
}
